import { useEffect, useState } from 'react';
import { customersService } from '../../services/customers.service';
import { portfolioService } from '../../services/portfolio.service';
import {
  PORTFOLIO_DISPLAY_CURRENCIES,
  listSnapshot,
  resolveCustomerPortfolio,
} from '../portfolio/portfolioModel';

// The Portfolio page defaults to this display currency, so the dashboard uses
// the same one: dashboard AUM must equal the Portfolio page total.
export const DASHBOARD_DISPLAY_CURRENCY = PORTFOLIO_DISPLAY_CURRENCIES[0];

const CUSTOMER_PAGE_SIZE = 100;
const MAX_CUSTOMER_PAGES = 10;
// Bounded fan-out: one consolidated-portfolio call per customer, a few at a
// time, so a dashboard load stays well inside the gateway rate limit.
export const MAX_PORTFOLIO_FANOUT = 50;
const FANOUT_CONCURRENCY = 4;

// Why the portal sums per-customer portfolios itself: GET /v1/portfolio/summary
// is backed by portfolio-v2 getPortfolioSummaryForOrg, which today returns the
// customer count but hardcodes every money field to 0 and the currency to USD
// (a documented TODO there). Once that RPC aggregates for real, switch the
// money figures back to it.

async function fetchAllCustomers() {
  let users = [];
  let cursor;
  for (let page = 0; page < MAX_CUSTOMER_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const result = await customersService.listCustomers({ limit: CUSTOMER_PAGE_SIZE, cursor });
    const pageUsers = Array.isArray(result?.users) ? result.users : [];
    users = users.concat(pageUsers);
    cursor = result?.nextCursor || null;
    if (!cursor) break;
  }
  return users;
}

export async function mapWithConcurrency(items, limit, fn) {
  const results = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const index = next;
      next += 1;
      // eslint-disable-next-line no-await-in-loop
      results[index] = await fn(items[index], index);
    }
  };
  const workers = Array.from({ length: Math.min(limit, items.length) }, worker);
  await Promise.all(workers);
  return results;
}

// Same per-customer read the Portfolio page does (resolveCustomerPortfolio +
// listSnapshot), so both pages agree to the cent.
export async function fetchCustomerSnapshot(customer, displayCurrency) {
  try {
    const { portfolio } = await resolveCustomerPortfolio(
      (id, options) => portfolioService.getCustomerPortfolio(id, options),
      customer,
      { displayCurrency },
    );
    return listSnapshot(portfolio, displayCurrency);
  } catch (err) {
    return { error: true };
  }
}

/**
 * Sum per-customer portfolio snapshots into org totals. Only snapshots priced
 * in the display currency are summed; anything else (a failed read, or a
 * fallback in another currency) is counted as unpriced rather than mixed in.
 */
export const aggregateSnapshots = (snapshots, displayCurrency) =>
  snapshots.reduce(
    (acc, snap) => {
      if (!snap || snap.error || (snap.currency && snap.currency !== displayCurrency)) {
        acc.unpricedCustomers += 1;
        return acc;
      }
      acc.pricedCustomers += 1;
      acc.totalPortfolioValue += snap.totalSum || 0;
      acc.totalMarketValue += snap.marketValue || 0;
      acc.totalCash += snap.cash || 0;
      acc.totalUnrealizedPnl += snap.unrealizedPnl || 0;
      if ((snap.positionCount || 0) > 0 || (snap.totalSum || 0) > 0) acc.investors += 1;
      return acc;
    },
    {
      totalPortfolioValue: 0,
      totalMarketValue: 0,
      totalCash: 0,
      totalUnrealizedPnl: 0,
      investors: 0,
      pricedCustomers: 0,
      unpricedCustomers: 0,
    },
  );

export async function loadOrgPortfolioTotals({ displayCurrency = DASHBOARD_DISPLAY_CURRENCY } = {}) {
  const [summaryResult, customersResult] = await Promise.allSettled([
    portfolioService.getOrgSummary(),
    fetchAllCustomers(),
  ]);
  const summary = summaryResult.status === 'fulfilled' ? summaryResult.value : null;
  const customers = customersResult.status === 'fulfilled' ? customersResult.value : [];

  const sampled = customers.slice(0, MAX_PORTFOLIO_FANOUT);
  const snapshots = await mapWithConcurrency(sampled, FANOUT_CONCURRENCY, (c) =>
    fetchCustomerSnapshot(c, displayCurrency),
  );
  const totals = aggregateSnapshots(snapshots, displayCurrency);

  const summaryCount = Number.isFinite(summary?.totalCustomers) ? summary.totalCustomers : null;
  const totalCustomers = summaryCount ?? customers.length;

  return {
    currency: displayCurrency,
    ...totals,
    totalCustomers,
    customersLoaded: customers.length,
    partial: customers.length > sampled.length || totals.unpricedCustomers > 0,
    failed: customersResult.status === 'rejected',
  };
}

export const useOrgPortfolioTotals = (displayCurrency = DASHBOARD_DISPLAY_CURRENCY) => {
  const [state, setState] = useState({ loading: true, totals: null });

  useEffect(() => {
    let active = true;
    loadOrgPortfolioTotals({ displayCurrency })
      .then((totals) => {
        if (active) setState({ loading: false, totals });
      })
      .catch(() => {
        if (active) setState({ loading: false, totals: null });
      });
    return () => {
      active = false;
    };
  }, [displayCurrency]);

  return state;
};
